"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { Checkbox, ColorField } from "@/ui";
import ImageUpload from "./ImageUpload";
import SettingsField from "./SettingsField";
import Header from "@components/Float/parts/Header";
import styles from "./Theme.module.css";

// The width the Float header is rendered at before being scaled to fit the
// panel. Theme.module.css mirrors this in the frame's aspect-ratio.
const HEADER_RENDER_WIDTH = 1100;

const MAX_HEADER_ITEMS = 3;

const HEADER_ITEMS = [
  { key: "phone", label: "Phone" },
  { key: "website", label: "Website" },
  { key: "linkedin", label: "LinkedIn" },
  { key: "instagram", label: "Instagram" },
];

const DISPLAY_OPTIONS = [
  {
    field: "show_bubbles",
    label: "Show bubble graphic on Float page header",
  },
  { field: "hide_recruiter_name", label: "Hide recruiter name on header" },
  { field: "use_small_photo", label: "Show smaller recruiter photo" },
];

export default function Theme({
  recruiter,
  formError,
  setRecruiter,
  setFormError,
  onFileUpload,
  mutation,
}) {
  const headerFrameRef = useRef(null);
  const [headerScale, setHeaderScale] = useState(0.6);

  // Scale the fixed-width header render to fill the panel's available width.
  // Only the inner transform is driven by this — the frame height comes from
  // CSS aspect-ratio — so there is no width/height feedback loop.
  useEffect(() => {
    const frame = headerFrameRef.current;
    if (!frame || typeof ResizeObserver === "undefined") {
      return undefined;
    }
    const observer = new ResizeObserver(([entry]) => {
      const next =
        Math.round((entry.contentRect.width / HEADER_RENDER_WIDTH) * 1000) /
        1000;
      setHeaderScale((prev) => (prev === next ? prev : next));
    });
    observer.observe(frame);
    return () => observer.disconnect();
  }, []);

  const handleImageUpdate = (image) => {
    onFileUpload();
  };

  // White is a valid hex but invisible on the Float page, so it's flagged as the
  // recruiter types rather than swallowed.
  const handleColorChange = (field) => (value) => {
    setRecruiter((prev) => ({ ...prev, [field]: value }));
    setFormError((prev) => {
      const next = { ...prev };
      if (value.toLowerCase() === "#ffffff") {
        next[field] = ["Please select a color other than white."];
      } else {
        delete next[field];
      }
      return next;
    });
  };

  // Laravel returns an array of messages per field; the /ui controls take one.
  const errorFor = (field) => {
    const messages = formError?.[field];
    if (!messages) return undefined;
    return Array.isArray(messages) ? messages[0] : messages;
  };

  const handleCheckboxChange = (field) => (checked) => {
    setRecruiter((prev) => ({
      ...prev,
      [field]: checked,
    }));
  };

  const headerItems = recruiter.float_header_items ?? [];
  // Only count selectable items toward the max — stale keys (e.g. a previously
  // saved "calendar") have no checkbox and must not block the others.
  const selectedHeaderCount = headerItems.filter((key) =>
    HEADER_ITEMS.some((item) => item.key === key),
  ).length;

  const previewRecruiter = useMemo(
    () => ({
      ...recruiter,
      name: recruiter.name || recruiter.user?.name || "",
      theme: {
        primary_color: recruiter.primary_color,
        secondary_color: recruiter.secondary_color,
        banner_image_url: recruiter.banner_image_url,
        show_bubbles: recruiter.show_bubbles,
        hide_recruiter_name: recruiter.hide_recruiter_name,
        use_small_photo: recruiter.use_small_photo,
      },
    }),
    [recruiter],
  );

  // Memoised so re-renders driven by the scale measurement don't re-render the
  // (heavy) Header tree — only the cheap transform on the wrapper changes.
  const headerPreview = useMemo(
    () => <Header recruiter={previewRecruiter} />,
    [previewRecruiter],
  );

  const handleHeaderItemToggle = (key) => (checked) => {
    setRecruiter((prev) => {
      const current = prev.float_header_items ?? [];
      const next = checked
        ? [...current, key]
        : current.filter((item) => item !== key);
      return { ...prev, float_header_items: next };
    });
  };

  return (
    <>
      <SettingsField
        label="Preview"
        description="See how your theme settings look on the header of a Float page. The preview reflects your current changes, even before you save."
      >
        <div className={styles.headerFrame} ref={headerFrameRef}>
          <div
            className={styles.headerScaler}
            style={{ transform: `scale(${headerScale})` }}
          >
            {headerPreview}
          </div>
        </div>
      </SettingsField>

      <SettingsField
        label="Float banner image"
        description="This is the banner image that appears at the top of interactive float pages. The image should be at least 1440 x 370px and in PNG or JPG format."
      >
        <ImageUpload
          recruiterId={recruiter.id}
          image={{ banner_image: recruiter.banner_image_url }}
          updateImage={handleImageUpdate}
          mutation={mutation}
          setFormError={setFormError}
          shape="rectangle"
        />
      </SettingsField>

      <SettingsField
        label="Primary accent color"
        description="This color is the primary accent color on interactive float pages we send to contacts. Used in design elements, buttons and links. Don't select white as it will not be visible."
      >
        <ColorField
          className={styles.colorField}
          value={recruiter.primary_color || ""}
          error={errorFor("primary_color")}
          onChange={handleColorChange("primary_color")}
        />
      </SettingsField>

      <SettingsField
        label="Secondary accent color"
        description="This is the secondary accent color on interactive float pages we send to contacts. Used in some design elements. Don't select white as it will not be visible."
      >
        <ColorField
          className={styles.colorField}
          value={recruiter.secondary_color || ""}
          error={errorFor("secondary_color")}
          onChange={handleColorChange("secondary_color")}
        />
      </SettingsField>

      <SettingsField
        label="Float display options"
        description="Configure style, content and actions for floats you share."
      >
        <div className={styles.checkGroup}>
          {DISPLAY_OPTIONS.map(({ field, label }) => (
            <Checkbox
              key={field}
              label={label}
              checked={Boolean(recruiter[field])}
              onChange={handleCheckboxChange(field)}
            />
          ))}
        </div>
      </SettingsField>

      <SettingsField
        label="Float header contents"
        description={`Choose up to ${MAX_HEADER_ITEMS} items to display in the header of your float pages. Only items that have a value set will be shown.`}
      >
        <div className={styles.checkGroup}>
          {HEADER_ITEMS.map(({ key, label }) => {
            const checked = headerItems.includes(key);
            return (
              <Checkbox
                key={key}
                label={label}
                checked={checked}
                disabled={!checked && selectedHeaderCount >= MAX_HEADER_ITEMS}
                onChange={handleHeaderItemToggle(key)}
              />
            );
          })}
        </div>
      </SettingsField>

    </>
  );
}
